import type {ContextRegionPayload} from '@github-ui/global-navigation-types'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useLayoutEffect, useRef, useState} from 'react'

import {
  type GlobalNavigationRefreshOptions,
  type GlobalNavigationRefreshResult,
  registerGlobalNavigationRefreshHandler,
} from '../global-navigation'

interface UseContextRegionRefreshOptions {
  contextRegion?: ContextRegionPayload
  contextRegionUrl?: string | null
}

export function useContextRegionRefresh({contextRegion, contextRegionUrl}: UseContextRegionRefreshOptions) {
  const [currentContextRegion, setCurrentContextRegion] = useState(contextRegion)
  const [syncedContextRegion, setSyncedContextRegion] = useState(contextRegion)
  const requestGenerationRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const invalidateRefresh = useCallback(() => {
    requestGenerationRef.current++
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
  }, [])

  // A new `contextRegion` prop comes from the parent route, so it is authoritative: it supersedes
  // any refresh that is still in flight, and it must be shown even if an earlier refresh failed.
  const hasNewContextRegionProp = !Object.is(syncedContextRegion, contextRegion)
  if (hasNewContextRegionProp) {
    setSyncedContextRegion(contextRegion)
    setCurrentContextRegion(contextRegion)
  }

  const activeContextRegion = hasNewContextRegionProp ? contextRegion : currentContextRegion

  const refreshContextRegion = useCallback(
    async ({
      path,
      crumbType,
      crumbObjectIdentifier,
      selectedLink,
      navigationCompletion,
    }: GlobalNavigationRefreshOptions): Promise<GlobalNavigationRefreshResult> => {
      const generation = ++requestGenerationRef.current
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      const navigationSucceeded = didNavigationSucceed(navigationCompletion)

      if (!contextRegionUrl) return 'failed'

      let destination: URL
      let url: URL
      try {
        destination = new URL(path, window.location.origin)
        url = new URL(contextRegionUrl, window.location.origin)
      } catch {
        return 'failed'
      }

      if (destination.origin !== window.location.origin) return 'failed'
      const destinationPath = `${destination.pathname}${destination.search}`

      url.searchParams.set('path', destinationPath)
      if (crumbType) url.searchParams.set('crumb_type', crumbType)
      if (crumbObjectIdentifier) url.searchParams.set('crumb_object_identifier', crumbObjectIdentifier)
      if (selectedLink) url.searchParams.set('selected_link', selectedLink)

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        const response = await reactFetchJSON(url.toString(), {signal: controller.signal})
        if (generation !== requestGenerationRef.current) return 'stale'
        if (!response.ok) return 'failed'

        const payload = (await response.json()) as ContextRegionPayload
        if (generation !== requestGenerationRef.current) return 'stale'
        if (payload.currentPath !== destinationPath) return 'failed'
        if (!(await navigationSucceeded)) return 'failed'
        if (generation !== requestGenerationRef.current) return 'stale'
        setCurrentContextRegion(payload)
        return 'applied'
      } catch {
        if (controller.signal.aborted || generation !== requestGenerationRef.current) return 'stale'
        return 'failed'
      }
    },
    [contextRegionUrl],
  )

  useLayoutEffect(() => {
    const unregister = registerGlobalNavigationRefreshHandler(refreshContextRegion)
    return () => {
      unregister()
      invalidateRefresh()
    }
  }, [invalidateRefresh, refreshContextRegion])

  useLayoutEffect(() => {
    // Invalidate and release any refresh that is still in flight, so a response for an older
    // destination or endpoint can't overwrite the current context.
    invalidateRefresh()
  }, [contextRegion, contextRegionUrl, invalidateRefresh])

  return {activeContextRegion}
}

async function didNavigationSucceed(navigationCompletion?: Promise<void>): Promise<boolean> {
  if (!navigationCompletion) return true

  try {
    await navigationCompletion
    return true
  } catch {
    return false
  }
}
